/**
 * ЕДИНСТВЕННОЕ место нового клиента, которое трогает `localStorage` и
 * `sessionStorage` (D-070, eslint). Всё остальное пишет через реестр записей.
 *
 * Хранилище бывает запрещено (приватный режим, политика), переполнено или
 * подменено расширением. Любая ошибка здесь — не повод уронить экран: запись
 * пропускается, чтение возвращает «нет записи» (D-073). Критическая работа и
 * так обязана жить на сервере.
 */
export interface KeyValueStorage {
  get(key: string): string | null
  set(key: string, value: string): boolean
  remove(key: string): void
  keys(): string[]
}

/** Обёртка над Web Storage, которая никогда не бросает. */
function safeStorage(resolve: () => Storage | undefined): KeyValueStorage {
  const storage = (): Storage | undefined => {
    try {
      return resolve()
    } catch {
      return undefined
    }
  }
  return {
    get(key) {
      try {
        return storage()?.getItem(key) ?? null
      } catch {
        return null
      }
    },
    set(key, value) {
      try {
        const target = storage()
        if (target === undefined) return false
        target.setItem(key, value)
        return true
      } catch {
        return false
      }
    },
    remove(key) {
      try {
        storage()?.removeItem(key)
      } catch {
        // запрет хранилища — удалять нечего
      }
    },
    keys() {
      try {
        const target = storage()
        if (target === undefined) return []
        const result: string[] = []
        for (let index = 0; index < target.length; index += 1) {
          const key = target.key(index)
          if (key !== null) result.push(key)
        }
        return result
      } catch {
        return []
      }
    },
  }
}

export interface BrowserStorages {
  session: KeyValueStorage
  device: KeyValueStorage
}

/** Хранилища браузера. `localStorage.clear()` не используется нигде (D-074). */
export function browserStorages(): BrowserStorages {
  return {
    session: safeStorage(() => globalThis.sessionStorage),
    device: safeStorage(() => globalThis.localStorage),
  }
}

/** Хранилище в памяти: тесты и окружения без Web Storage. */
export function memoryStorage(): KeyValueStorage {
  const data = new Map<string, string>()
  return {
    get: (key) => data.get(key) ?? null,
    set: (key, value) => {
      data.set(key, value)
      return true
    },
    remove: (key) => {
      data.delete(key)
    },
    keys: () => [...data.keys()],
  }
}
